/**
 * Coming-soon / pre-launch status. Fetched once from the backend (not baked
 * in at build time) so flipping COMING_SOON_MODE on the server takes effect
 * without a frontend rebuild. A successful answer is cached at module scope
 * for the tab's lifetime — a FAILED check is never cached, so a transient
 * blip (one dropped request, hitting the site mid-restart) only affects the
 * component that hit it, not every later mount in the tab. Without this, a
 * single failure used to wrongly and permanently show "registration open"
 * (Create account button, full signup form) for the rest of the tab's life,
 * even once the backend was answering fine again.
 */
import { useEffect, useState } from 'react';
import { earlyAccessApi } from './api';

let cache = null; // { comingSoon } once a check has actually succeeded

const RETRY_DELAYS_MS = [500, 1500]; // a couple of quick retries before giving up

async function fetchComingSoon() {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      const { data } = await earlyAccessApi.status();
      return Boolean(data.coming_soon);
    } catch (e) {
      if (attempt === RETRY_DELAYS_MS.length) throw e;
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
    }
  }
  return undefined; // unreachable, keeps eslint happy about consistent return
}

export function useLaunchStatus() {
  const [comingSoon, setComingSoon] = useState(cache?.comingSoon ?? null);

  useEffect(() => {
    if (cache) {
      setComingSoon(cache.comingSoon);
      return undefined;
    }
    let live = true;
    (async () => {
      try {
        const result = await fetchComingSoon();
        cache = { comingSoon: result };
        if (live) setComingSoon(result);
      } catch {
        // Still couldn't reach it after retries — don't block the normal
        // site behind it, but leave `cache` unset so the NEXT mount (a new
        // page, a new tab) tries again instead of being stuck on this
        // fallback for the tab's whole lifetime.
        if (live) setComingSoon(false);
      }
    })();
    return () => { live = false; };
  }, []);

  return { comingSoon, loading: comingSoon === null };
}
